import {
  createSlice,
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit";
import { toast } from "sonner";
import { AxiosError } from "axios";
import apiService from "../../services/api.services";

// Types
export interface Question {
  id: number;
  questionText: string;
  options: {
    text: string;
    isCorrect: boolean;
  }[];
}

export interface Template {
  id?: number;
  title: string;
  instructions: string;
  timeLimit: number;
  courseId: number | any;
  moduleId?: number | null;
  passMark?: number;
  attemptLimit?: number;
  isActive?: boolean | number;
  questions: Question[];
  createdBy?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface TemplateQueryParams {
  page?: number;
  limit?: number;
  createdBy?: number;
  isActive?: boolean;
}

interface FetchTemplatesResponse {
  templates: Template[];
  pagination: {
    total: number;
    currentPage: number;
    totalPages: number;
  };
}

interface TemplateState {
  templates: Record<number, Template>;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  total: number;
  currentPage: number;
  lastFetched: Record<string, number>;
  activeTemplate: Template | null;
}

// Initial state
const initialState: TemplateState = {
  templates: {},
  status: "idle",
  error: null,
  total: 0,
  currentPage: 1,
  lastFetched: {},
  activeTemplate: null,
};

// Helper function for creating cache keys
const createCacheKey = (params: TemplateQueryParams): string => {
  return Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${value}`)
    .join("|");
};

// Async Thunks
export const fetchTemplates = createAsyncThunk<
  { templates: Template[]; pagination: any; cacheKey: string },
  TemplateQueryParams,
  { rejectValue: { message: string } }
>("templates/fetchTemplates", async (params, { rejectWithValue }) => {
  try {
    const queryParams = new URLSearchParams({
      page: String(params.page || 1),
      limit: String(params.limit || 10),
      ...(params.createdBy && { createdBy: String(params.createdBy) }),
      ...(params.isActive && { isActive: String(params.isActive) }),
    });

    const response = await apiService.get<FetchTemplatesResponse>(
      `/qa/templates?${queryParams}`
    );

    return {
      ...response,
      cacheKey: createCacheKey(params),
    };
  } catch (error) {
    const axiosError = error as AxiosError<{ message: string }>;
    toast.error(
      axiosError.response?.data?.message || "Failed to fetch templates"
    );
    return rejectWithValue({
      message:
        axiosError.response?.data?.message || "Failed to fetch templates",
    });
  }
});

export const createTemplate = createAsyncThunk<
  Template,
  Template,
  { rejectValue: { message: string } }
>("templates/createTemplate", async (template, { rejectWithValue }) => {
  try {
    const response = await apiService.post<Template>(
      `/qa/create-template`,
      template
    );
    toast.success("Template created successfully");
    return response;
  } catch (error) {
    const axiosError = error as AxiosError<{ message: string }>;
    toast.error(
      axiosError.response?.data?.message || "Failed to create template"
    );
    return rejectWithValue({
      message:
        axiosError.response?.data?.message || "Failed to create template",
    });
  }
});

export const updateTemplate = createAsyncThunk<
  Template,
  { id: number; updates: Partial<Template> },
  { rejectValue: { message: string } }
>("templates/updateTemplate", async ({ id, updates }, { rejectWithValue }) => {
  try {
    const response = await apiService.patch<Template>(
      `/qa/templates/${id}/toggle-status`,
      updates
    );

    return response;
  } catch (error) {
    const axiosError = error as AxiosError<{ message: string }>;
    toast.error(
      axiosError.response?.data?.message || "Failed to update template"
    );
    return rejectWithValue({
      message:
        axiosError.response?.data?.message || "Failed to update template",
    });
  }
});

export const deleteTemplate = createAsyncThunk<
  number,
  number,
  { rejectValue: { message: string } }
>("templates/deleteTemplate", async (id, { rejectWithValue }) => {
  try {
    await apiService.delete(`/templates/${id}`);
    toast.success("Template deleted successfully");
    return id;
  } catch (error) {
    const axiosError = error as AxiosError<{ message: string }>;
    toast.error(
      axiosError.response?.data?.message || "Failed to delete template"
    );
    return rejectWithValue({
      message:
        axiosError.response?.data?.message || "Failed to delete template",
    });
  }
});

// Slice
const templateSlice = createSlice({
  name: "templates",
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setActiveTemplate: (state, action: PayloadAction<Template | null>) => {
      state.activeTemplate = action.payload;
    },
    resetTemplateError: (state) => {
      state.error = null;
    },
    resetTemplates: (state) => {
      state.templates = {};
    },
    updateActiveTemplate: (state, action: PayloadAction<Partial<Template>>) => {
      if (state.activeTemplate) {
        state.activeTemplate = {
          ...state.activeTemplate,
          ...action.payload,
        };
      }
    },
    addQuestionToActive: (state) => {
      if (state.activeTemplate) {
        const newId =
          Math.max(...state.activeTemplate.questions.map((q) => q.id), 0) + 1;
        state.activeTemplate.questions.push({
          id: newId,
          questionText: "",
          options: [
            { text: "", isCorrect: false },
            { text: "", isCorrect: false },
            { text: "", isCorrect: false },
            { text: "", isCorrect: false },
          ],
        });
      }
    },
    removeQuestionFromActive: (state, action: PayloadAction<number>) => {
      if (state.activeTemplate && state.activeTemplate.questions.length > 1) {
        state.activeTemplate.questions = state.activeTemplate.questions.filter(
          (q) => q.id !== action.payload
        );
      }
    },
    updateQuestionInActive: (
      state,
      action: PayloadAction<{
        questionId: number;
        updates: Partial<Question>;
      }>
    ) => {
      if (state.activeTemplate) {
        state.activeTemplate.questions = state.activeTemplate.questions.map(
          (q) =>
            q.id === action.payload.questionId
              ? { ...q, ...action.payload.updates }
              : q
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch templates
      .addCase(fetchTemplates.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        const { templates, pagination, cacheKey } = action.payload;
        state.templates = templates.reduce(
          (acc, template) => ({
            ...acc,
            [template.id!]: {
              ...template,
              isActive: template.isActive === 1 ? true : false,
            },
          }),
          {}
        );
        state.total = pagination.total;
        state.currentPage = pagination.currentPage;
        state.lastFetched[cacheKey] = Date.now();
        state.status = "succeeded";
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to fetch templates";
      })

      // Create template
      .addCase(createTemplate.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.templates[action.payload.id!] = action.payload;
        state.status = "succeeded";
        state.activeTemplate = null;
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to create template";
      })

      // Update template
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.templates[action.payload.id!] = action.payload;
        state.activeTemplate = null;
      })

      // Delete template
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        delete state.templates[action.payload];
      });
  },
});

// Actions
export const {
  setCurrentPage,
  setActiveTemplate,
  resetTemplateError,
  updateActiveTemplate,
  addQuestionToActive,
  removeQuestionFromActive,
  updateQuestionInActive,
  resetTemplates
} = templateSlice.actions;

// Selectors
export const selectTemplates = createSelector(
  [(state: { templates: TemplateState }) => state.templates.templates],
  (templates): Template[] => Object.values(templates)
);

export const selectTemplateById = createSelector(
  [
    (state: { templates: TemplateState }) => state.templates.templates,
    (_: any, id: number) => id,
  ],
  (templates, id): Template | undefined => templates[id]
);

export const selectActiveTemplate = (state: {
  templates: TemplateState;
}): Template | null => state.templates?.activeTemplate;

export default templateSlice.reducer;
